import React, { useContext, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import cn from 'classnames'
import FavoritePoseItem from '../../../../../favorite-pose-item'
import styles from './FavoritePoseSelection.module.sass'
import Typography from '../../../../../Typography'
import Button from '../../../../../Button'
import classes from '../../../../favorite-pose-selection/favorite-pose-selection.module.sass'
import CloseIcon from '../../../../../svg-icons/close-icon'
import PreviewImage from '../../../../../PreviewImage'
import Modal from '../../../../../modal'
import { routes } from '../../../../../../routes'
import heartIcon from '../../../../../../assets/favorite_all.png'
import fullHeartIcon from '../../../../../../assets/heart.png'
import Image from "../../../../../Image";
import useImageOrientations from '../../../../../../hooks/useImageOrientations';
import usePackagePoses from '../../../../../../hooks/usePackagePoses';
import { useIsFetching } from '@tanstack/react-query';
import background from '../../../../../../models/Background';
import usePricelist from '../../../../../../hooks/usePricelist';
import { CustomerContext } from '../../../../../../contexts/CustomerContext'
import useBuyAllOption from "../../../../../../hooks/useBuyAllOption";
import axios from 'axios';
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import useAddons from "../../../../../../hooks/useAddons";

// Case-insensitive comparator for season values. Production data has mixed
// casing (e.g. package_poses.season = "Spring2026" while
// student_images.folder = "spring2026"), so a strict === silently mismatches
// the spring side and falls back to backgrounds[0].
const seasonMatches = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

export const getAppropriateBg = (packagePoses, pose, backgrounds, pricelist) => {
  if (!packagePoses || packagePoses.length === 0) return backgrounds[0];

  const poseNumber = pose.url.split('-').reverse()[0].split('.')[0];

  const preferred = packagePoses.find((packagePose) => {
    if (pricelist?.multipleSeasons?.length) {
      return packagePose.poseNumber === poseNumber && seasonMatches(packagePose.season, pose.folder);
    }

    return packagePose.poseNumber === poseNumber;
  });

  if (!preferred) return backgrounds[0];

  return preferred.background;
};

export const getAppropriateForeground = (packagePoses, pose, pricelist) => {
  if (!packagePoses || packagePoses.length === 0) return undefined;

  const poseNumber = pose.url.split('-').reverse()[0].split('.')[0];

  const preferred = packagePoses.find((packagePose) => {
    if (pricelist?.multipleSeasons?.length) {
      return packagePose.poseNumber === poseNumber && seasonMatches(packagePose.season, pose.folder);
    }

    return packagePose.poseNumber === poseNumber;
  });

  if (!preferred) return undefined;

  return preferred.foreground;
};

const renderInstructions = () => (
  <div className={styles.instructions}>
    <Typography align="center" weight="bold">
      Choose your Favorites
    </Typography>
  </div>
)

// The redesigned header: "Melina's Third Grade Portraits" over a "Choose Your Favorites"
// banner, matching the senior gallery. Opt-in via the `headline` prop — this component is
// shared with several other flows (sittings, individual prints, past images), and they
// keep the plain instruction line until they're redesigned too.
const renderHeader = (headline) => (
  <>
    <div className="bg-white py-8 text-center rounded-b-lg shadow-sm">
      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">{headline}</h1>
    </div>
    <div className="mt-2 bg-shoob-blue text-white rounded-md px-5 py-4 text-center">
      <p className="text-lg leading-tight font-semibold">Choose Your Favorites</p>
    </div>
  </>
);

const renderModal = (modalImage, bg, foreground, onClick) => (
  <Modal open>
    <div className={classes.modalContent}>
      <div className={classes.iconWrapper}>
        <CloseIcon className={classes.closeIcon} onClick={onClick} />
      </div>
      <PreviewImage foregroundUrl={modalImage} backgroundUrl={bg?.imageUrl || backgrounds[0]?.imageUrl} actualForegroundUrl={foreground?.imageUrl} />
    </div>
  </Modal>
)

/**
 * Component
 */
const FavoritePoseSelection = ({
  background, backgrounds, images = [], seniors = false, headline = null,
}) => {
  const history = useHistory()
  const { packageId } = useParams()
  const [modalImage, setModalImage] = useState()
  const [favorites, setFavorites] = useState([])
  const { data: pricelist } = usePricelist(packageId);
  const { data: packagePoses } = usePackagePoses(packageId);
  const [image, setImage] = useState();
  const { customer, setCustomer } = useContext(CustomerContext)
  const { data: buyAllOption } = useBuyAllOption(packageId, customer?.currentSchool?.id);
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`)
  const { data: addons } = useAddons(!seniors);

  // prime the url cache to toggle between landscape and portrait backgrounds
  useImageOrientations((backgrounds || []).map((bg) => bg.imageUrl));

  // Filter images to only show poses configured in the package
  const filteredImages = packagePoses && packagePoses.length > 0
    ? images.filter((image) => {
        const poseNumber = image.url.split('-').reverse()[0].split('.')[0];
        return packagePoses.some((pp) => pp.poseNumber === poseNumber);
      })
    : images;

  // Check if all poses are JPG
  const allPosesAreJpg = filteredImages.length > 0 && filteredImages.every(image =>
    image.imageUrl && image.imageUrl.toLowerCase().includes('.jpg')
  );

  const onFavoriteClick = (id) => {
    if (favorites.includes(id)) setFavorites((prev) => prev.filter((item) => item !== id))
    else setFavorites((prev) => [...prev, id])
  }

  const favoriteAll = () => {
    setFavorites(filteredImages.map((image) => image.id))
  }

  const handleBuyAll = async () => {
    const response = await axios.post(`/carts/${customer?.cart?.id}/buy_all`,
      { package_id: packageId, option_id: buyAllOption.id, student_id: customer.currentStudent.id, season: currentSeason }
    );

    const orderPackageId = response.data?.order_package_id;

    history.push(routes.confirm(orderPackageId))
  }

  const onContinue = () => {
    if (favorites.length === 0) {
      alert('Please select a pose to continue to the next page')
      return
    }
    if (seniors) {
      // Senior flow inserts a REQUIRED yearbook-pose step before packages so every
      // senior submits a yearbook pose of record in one pass. Elementary is unchanged.
      history.push(routes.seniors.packageBuilder(packageId, {favorites, step: 'yearbook-pose'}));
    } else {
      history.push(routes.elementaryMultiPoseBuilder(packageId, {favorites, step: 'select-package'}));
    }
  }

  const bgForModal = (foreground, pricelist) => {
    const priorityBg = getAppropriateBg(packagePoses, foreground, backgrounds, pricelist);

    return priorityBg || backgrounds[0];
  };

  const foregroundForModal = (foreground, pricelist) => {
    const f = getAppropriateForeground(packagePoses, foreground, pricelist);

    return f;
  };

  const handleImageClick = (foreground, foregroundUrl) => {
    setModalImage(foregroundUrl);
    setImage(foreground);
  }

  const isFetching = useIsFetching();

  if (isFetching) return null;

  return (
    <div style={{ maxWidth: '992px', margin: '0 auto' }}>
      <div className={styles.imageContainer}>
        {headline ? renderHeader(headline) : renderInstructions()}

        {modalImage && renderModal(modalImage, bgForModal(image, pricelist), foregroundForModal(image, pricelist), () => setModalImage(undefined))}

        <div className={cn(styles.container, modalImage && styles.hidden, filteredImages.length === 1 ? styles.onePoseContainer : '')}>
          {buyAllOption && !allPosesAreJpg && (
            <div onClick={handleBuyAll}>
              <PreviewImage
                containerClass={styles.buyAll}
                foregroundUrl={buyAllOption.buyAllBannerUrl}
              />
            </div>
          )}

          {filteredImages.map((image, i) => {
            let bg = backgrounds ? backgrounds[i % backgrounds.length].imageUrl : background;
            const priorityBg = getAppropriateBg(packagePoses, image, backgrounds, pricelist);
            const foreground = getAppropriateForeground(packagePoses, image, pricelist);

            return (
              <FavoritePoseItem
                index={i}
                key={image.id}
                onImageClick={handleImageClick}
                background={bg}
                priorityBg={priorityBg}
                backgrounds={backgrounds || []}
                onFavoriteClick={onFavoriteClick}
                id={image.id}
                foreground2={image}
                foreground={image.watermarkUrl}
                actualForeground={foreground?.imageUrl}
                fileName={image.imageFileName}
                favorited={favorites.includes(image.id)}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.btnContainer}>
        <Button
          bold
          circular
          fullWidth
          onClick={favoriteAll}
          variant="primary"
          classes={styles.favoriteAllButton}
        >
          <span className={styles.favoriteAll}>
            {favorites.length === filteredImages.length ? (
              <Image src={fullHeartIcon} alt="Favorite this image" classes={styles.favoriteAllIcon} />
            ) : (
              <Image src={heartIcon} alt="Favorite this image" classes={styles.favoriteAllIcon} />
            )}{' '} Favorite All
          </span>
        </Button>

        <Button
          bold
          circular
          fullWidth
          onClick={onContinue}
          variant="primary"
          classes={favorites.length === 0 ? classes.lowOpacity : ''}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}

export default FavoritePoseSelection
